import type { ScanedDetails } from './scannedDetails'

const REQUIRED_POINTS = 6
const MAX_PRICE_SPREAD = 5

// "EXCHANGE:SYMBOL" — OCR sometimes reads the colon as ";". Both parts must contain
// letters so timestamps like "09:38" in the chart header are not picked up.
export const SYMBOL_REGEX = /\b([A-Z][A-Z0-9_]+)\s*[:;]\s*([A-Z0-9_.!]*[A-Z][A-Z0-9_.!]*)/
// "PRICE-TIMESTAMP" (unix seconds, 10 digits), dash sometimes read as a space
const PAIR_REGEX = /(\d+(?:\.\d+)?)(?:\s*-+\s*|\s+)(\d{10})(?!\d)/
// Same as above when the dash was dropped by OCR: the last 10 digits are the timestamp
const PAIR_WITHOUT_DASH_REGEX = /(\d*\.\d+?)(\d{10})(?!\d)/

// Characters OCR commonly confuses with digits/dashes on the price lines
const normalizePriceLine = (line: string) =>
  line
    .replace(/[Oo]/g, '0')
    .replace(/[Il|!]/g, '1')
    .replace(/[Ss]/g, '5')
    .replace(/[B]/g, '8')
    .replace(/[Z]/g, '2')
    .replace(/,/g, '.')
    .replace(/[–—~_=]/g, '-')
    .replace(/[^\d.\-\s]/g, '')
    .replace(/\s+/g, ' ')

export const parsePricePoint = (line: string) => {
  const normalized = normalizePriceLine(line)
  const match = normalized.match(PAIR_REGEX) ?? normalized.match(PAIR_WITHOUT_DASH_REGEX)

  if (!match) {
    return null
  }

  const price = Number(match[1])
  const time = Number(match[2])

  if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(time)) {
    return null
  }

  return { price, time }
}

/**
 * Parses the OCR text of the HVF Drawing Tool data box:
 *
 *   COINBASE:XLMUSD
 *   0.234165-1781542800   (H1)
 *   0.208193-1781575200   (L1)
 *   ...                   (H2, L2, H3, L3)
 *
 * Lines are matched by pattern instead of position so blank lines, axis labels
 * or other noise caught in the crop don't shift the values.
 */
export const extractHvfDetails = (rawOcrResult: string): ScanedDetails | null => {
  const lines = rawOcrResult
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  let exchange = ''
  let symbol = ''
  const points: { price: number; time: number }[] = []

  for (const line of lines) {
    // Keep the symbol line closest above the price list (ignores chart header text)
    if (points.length === 0) {
      const symbolMatch = line.toUpperCase().match(SYMBOL_REGEX)
      if (symbolMatch) {
        exchange = symbolMatch[1]
        symbol = symbolMatch[2]
        continue
      }
    }

    if (points.length < REQUIRED_POINTS) {
      const point = parsePricePoint(line)
      if (point) {
        points.push(point)
      }
    }
  }

  if (points.length < REQUIRED_POINTS) {
    return null
  }

  // HVF extremes sit in a narrow range; a big spread means OCR dropped a decimal point
  const prices = points.map((point) => point.price)
  if (Math.max(...prices) / Math.min(...prices) > MAX_PRICE_SPREAD) {
    return null
  }

  const [high1, low1, high2, low2, high3, low3] = points

  return {
    symbol,
    exchange,
    high_1_price: high1.price,
    high_1_time: high1.time,
    high_2_price: high2.price,
    high_2_time: high2.time,
    high_3_price: high3.price,
    high_3_time: high3.time,
    low_1_price: low1.price,
    low_1_time: low1.time,
    low_2_price: low2.price,
    low_2_time: low2.time,
    low_3_price: low3.price,
    low_3_time: low3.time,
  }
}
